import type { DocumentData } from "firebase/firestore/lite";
import type { FormikProps } from "formik";
import type React from "react";
import { type FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type getPhaseOfDateRange, useAuthContext } from "senf-shared";
import type { Project, Survey, User } from "senf-shared/types";
import { toSentenceCase } from "senf-shared/utils/convertCase";
import LanguageButton from "../../../components/molecules/languageSettings/LanguageButton";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import type { IconName } from "../../core/icon/names";
import ProgressBar from "../../core/progressBar/ProgressBar";
import Toast from "../../core/toast/Toast";
import AccessibilitySettings from "../accessibilitySettings/AccessibilitySettings";
import SurveyWrapper from "../survey/SurveyWrapper";
import { useNavigationIndex, usePrepareSurveyPage } from "../survey/hooks";
import type { Question, Result, Settings } from "../survey/survey.types";
import SurveyGuide from "./components/SurveyGuide";
import type { PageType } from "./pageTypes";
import { validMapInput } from "./utils";

interface ViewSurveyProps {
  mode: "preview" | undefined;
  survey: Survey;
  formik: FormikProps<Result["values"]>;
  loginFormik: FormikProps<{
    email: string;
    password: string;
  }>;
  demographicsFormik: FormikProps<User>;
  project: Project;
  outroButtons?: {
    text: string;
    onClick: () => void;
  }[];
  handleClose: () => void;
  submitting: boolean;
  setSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  phase: ReturnType<typeof getPhaseOfDateRange>;
  handleSaveNext: (props: {
    values: Result["values"];
    completed?: boolean;
  }) => void;
  handleProcessNext: (user?: DocumentData) => Promise<boolean>;
  index: number;
  setIndex: React.Dispatch<React.SetStateAction<number>>;
  variant?: "fullscreen" | "default";
  surveyMode?: "test" | "preview" | null;
  surveyLoginFormShown?: boolean;
  setSurveyLoginFormShown?: React.Dispatch<React.SetStateAction<boolean>>;
  user?: DocumentData;
}
interface NavigationButtonProps {
  disabled: boolean;
  disabledNotice: string;
  show: boolean;
  onClick: () => void;
  loading?: boolean;
  trailingIcon?: IconName;
  text?: string;
  tooltipLabel?: string;
  ariaLabel?: string;
}
interface NavigationButtonsProps extends React.HTMLAttributes<HTMLDivElement> {
  prev: NavigationButtonProps;
  next: NavigationButtonProps;
}
const NavigationButtons = ({
  prev,
  next,
  className,
  extraButton,
}: NavigationButtonsProps) => {
  const { t } = useTranslation();
  const { show: showPrev, ariaLabel: ariaLabelPrev, ...prevProps } = prev;
  const { show: showNext, ariaLabel: ariaLabelNext, ...nextProps } = next;

  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: we are in a form and using fieldset here could be confising
      role="group"
      className={cn(
        "fixed bottom-0 flex items-center gap-2 p-5 end-0",
        className,
      )}
    >
      {extraButton}
      {/* {showNext !== false && !nextProps.disabled && (
        <Button
          variant="shapeless"
          leadingIcon="enter"
          text={t("util:pressKey", { key: t("global:enter") })}
          size="sm"
          className="text-text-subdued pointer-events-none"
          tabIndex={-1}
        />
      )} */}
      {showPrev !== false && (
        <Button
          size="lg"
          leadingIcon="arrowhead-left-md"
          aria-label={ariaLabelPrev}
          {...prevProps}
        />
      )}
      {showNext !== false && (
        <Button
          variant="primary"
          size="lg"
          id="take-survey-continue-button"
          aria-label={ariaLabelNext}
          {...nextProps}
        />
      )}
    </div>
  );
};

interface CloseSurveyProps extends ViewSurveyProps {
  fullscreenMap: boolean;
}

const CloseSurvey = ({
  fullscreenMap,
  handleClose,
  settings,
  project,
  mode,
}: CloseSurveyProps) => {
  const { t } = useTranslation();
  // useOnKeyDown((e) => {
  //   if (e.code === "Escape") {
  //     handleClose();
  //   }
  // });
  if (
    !handleClose ||
    (mode !== "preview" &&
      (fullscreenMap || settings?.publicAccess === false || !project?.title))
  )
    return undefined;

  return (
    <Button
      leadingIcon="x-md"
      tooltipLabel={t("util:close", { val: t("global:survey") })}
      onClick={handleClose}
      dataCy="take-survey-close-button"
      classNameWrapper="fixed top-0 z-50 m-5 end-0"
    />
  );
};

const tooFew = (num: number, minChoices: Question["minChoices"]) =>
  num < (minChoices || Number.NEGATIVE_INFINITY);
const tooMany = (num: number, maxChoices: Question["maxChoices"]) =>
  num > (maxChoices || Number.POSITIVE_INFINITY);
const isNullish = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === "";
interface NextDisabledProps {
  pages: Question[];
  index: number;
  formik: FormikProps<Result>;
  demographicsFormik?: FormikProps<User>;
}
const useNextDisabled = ({
  pages,
  index,
  formik,
  demographicsFormik,
}: NextDisabledProps) => {
  const { t, i18n } = useTranslation();
  const message = useRef("");
  let disabled = false;

  const {
    required,
    textResponseRequired,
    qtype,
    uid,
    options,
    minChoices,
    maxChoices,
    minValue,
    maxValue,
    maxChar,
    multipleChoice,
    mapbasedQtype,
    mapbasedFeatureLimits,
    minFeatures,
    maxFeatures,
  } = pages[index] || {};

  // if (!required || !qtype) {
  //   return {
  //     disabled,
  //     message,
  //   };
  // }
  const { value, comment, geoData } = formik.values[uid] || {};
  const valueArray = Object.values(value || {}) as (string | number | null)[];
  const numChoices = multipleChoice ? value?.length || 0 : value ? 1 : 0;
  const numChoicesValueArray = valueArray?.length || 0;

  const schema: Record<
    string,
    (
      value?: string | Record<string, string | string[] | { value: string }>,
    ) => boolean | string
  > = {
    demographics: () => {
      if (!demographicsFormik?.isValid) {
        try {
          return toSentenceCase(
            new Intl.ListFormat(i18n.resolvedLanguage, {
              style: "long",
              type: "conjunction",
            }).format(Object.values(demographicsFormik?.errors || {})),
          );
        } catch (error) {
          return t("global:fillInAllFields");
        }
      }
      return false;
    },
    text: (value) => {
      if (
        required &&
        (options?.length || 0) === 1 &&
        options?.some((e) => !value?.[e?.uid]?.value?.length)
      ) {
        return t("survey:disabledMessages.text");
      }
      if (
        required &&
        (options?.length || 0) > 1 &&
        options?.some((e) => !value?.[e?.uid]?.value?.length)
      ) {
        return t("survey:disabledMessages.text.everyRow");
      }
      if (
        maxChar &&
        options?.some((e) => (value?.[e.uid]?.value?.length || 0) > maxChar)
      )
        return t("survey:disabledMessages.text.maxChar", { maxChar });
      return false;
    },
    number: (value) => {
      const valueArray = Object.values(value || {}).map((e) => e?.value);
      const numChoices = options?.length || 1;
      const [min, max] = [minValue, maxValue];

      if (required && options?.some((e) => !value?.[e?.uid]?.value)) {
        if (numChoices > 1) return t("survey:disabledMessages.number.everyRow");
        return t("survey:disabledMessages.number");
      }
      if (
        valueArray.some(
          (e) =>
            (!isNullish(e) || required) &&
            ((!isNullish(min) && e < min) || (!isNullish(max) && e > max)),
        )
      ) {
        return t("survey:disabledMessages.number.minMax", {
          min,
          max,
        });
      }
      if (
        !isNullish(min) &&
        valueArray.some((e) => (!isNullish(e) || required) && e < min)
      ) {
        return t("survey:disabledMessages.number.min", { min });
      }
      if (
        !isNullish(max) &&
        valueArray.some((e) => (!isNullish(e) || required) && e > max)
      ) {
        return t("survey:disabledMessages.number.max", { max });
      }
      return false;
    },
    budget: () =>
      !!required &&
      (!value || !numChoices) &&
      t("survey:disabledMessages.checkbox"),
    radio: (value) => {
      const numChoices = multipleChoice
        ? value?.length
          ? Number(value.length)
          : 0
        : value
          ? 1
          : 0;

      if (required && !multipleChoice && !numChoices)
        return t("survey:disabledMessages.radio");
      if (tooFew(numChoices, minChoices))
        return t("survey:disabledMessages.radio.minChoices", {
          count: minChoices,
        });
      // THE DISABLED LOGIC IS MOVED TO THE RADIO
      if (tooMany(numChoices, maxChoices))
        return t("survey:disabledMessages.radio.maxChoices", {
          count: maxChoices,
        });
      return false;
    },
    radioMatrix: (value) => {
      const valueArray = Object.values(value || {});
      const numChoicesValueArray = valueArray?.length || 0;

      if (
        required &&
        (!valueArray || numChoicesValueArray < (options?.length || 0))
      )
        return t("survey:disabledMessages.radioMatrix");
      if (
        multipleChoice &&
        valueArray.some((e) => tooFew(e.length, minChoices))
      )
        return t("survey:disabledMessages.radioMatrix.minChoices", {
          count: minChoices,
        });
      // THE DISABLED LOGIC IS MOVED TO THE RADIOMATRIX
      if (
        multipleChoice &&
        valueArray.some((e) => tooMany(e.length, maxChoices))
      )
        return t("survey:disabledMessages.radioMatrix.maxChoices", {
          count: maxChoices,
        });
      return false;
    },
    rating: () => {
      // if (!value || !Object.values(value).some(Boolean))
      if (
        required &&
        (!valueArray || numChoicesValueArray < (options?.length || 0))
      )
        return t("survey:disabledMessages.ratingScale");
      if (textResponseRequired && !comment)
        return t("survey:disabledMessages.ratingScale.textarea");
      if (maxChar && comment?.length > maxChar)
        return t("survey:disabledMessages.text.maxChar", { maxChar });
      return false;
    },
    map: (type?: "point" | "path" | "area") => {
      const geoData = formik.values[uid]?.geoData;
      const geoDataObj = geoData && JSON.parse(geoData);
      const features = geoDataObj?.features;

      if (required && (!geoData || !features) && !mapbasedFeatureLimits) {
        return t(`survey:disabledMessages.${type}`);
      }

      if (
        required &&
        (mapbasedQtype === "textarea" || mapbasedQtype === "radio") &&
        features &&
        !validMapInput(features, mapbasedQtype)
      ) {
        return t("global:addAllRequiredData");
      }

      if (
        required &&
        (!features?.length ||
          (mapbasedFeatureLimits &&
            minFeatures &&
            minFeatures > features?.length))
      ) {
        const getFeatureType = (type: string, count: number) => {
          return t(count === 1 ? `global:${type}` : `global:${type}s`);
        };

        const minMaxFeatureKey =
          minFeatures && maxFeatures && minFeatures === maxFeatures
            ? "youMustAddExactlyFeatures"
            : minFeatures && maxFeatures
              ? "youMustAddMinMaxFeatures"
              : !minFeatures && maxFeatures
                ? "youMustAddMaxFeatures"
                : minFeatures && !maxFeatures
                  ? "youMustAddMinFeatures"
                  : "youCanAddMultipleFeatures";

        return t(`survey:options.${minMaxFeatureKey}`, {
          count: !mapbasedFeatureLimits ? 1 : minFeatures || maxFeatures || 2,
          maxCount: maxFeatures,
          featureType: getFeatureType(
            type,
            !mapbasedFeatureLimits ? 2 : maxFeatures || minFeatures || 2,
          ),
        });
      }

      //THIS CAN ACTUALLY NEVER HAPPEN
      if (
        mapbasedFeatureLimits &&
        maxFeatures &&
        maxFeatures < features?.length
      ) {
        return t(`survey:disabledMessages.map.maxFeatures`, { maxFeatures });
      }

      return false;
    },
  };

  const schemaMap: Record<PageType, any> = {
    ...schema,
    imageChoice: schema.radio,
    ratingScale: schema.rating,
    stars: schema.rating,
    slider: schema.rating,
    point: () => schema.map("point"),
    path: () => schema.map("path"),
    area: () => schema.map("area"),
  };

  message.current = schemaMap[qtype]?.(value);
  disabled = !!message.current?.length;

  return { disabled, message };
};

interface SurveyNavigationButtonsProps extends ViewSurveyProps {
  currentPage: Question;
  currentPageRole: string;
  setFullscreenMap: React.Dispatch<React.SetStateAction<boolean>>;
  featureId?: string;
  isMobile: boolean;
}

const SurveyNavigationButtons = ({
  setSurveyLoginFormShown,
  surveyLoginFormShown,
  demographicsFormik,
  loginFormik,
  currentPageRole,
  survey,
  phase,
  formik,
  index,
  setFullscreenMap,
  submitting,
  setSubmitting,
  featureId,
  openDescription,
  setIndex,
  handleProcessNext,
  handleSaveNext,
  isMobile,
  project,
  currentPage,
}: SurveyNavigationButtonsProps) => {
  const { t } = useTranslation();
  const { signIn, createUser } = useAuthContext();
  const { pages, formattedEndDate, formattedStartDate } = survey;
  const { nextIndex, previousIndex, isLastPage } = useNavigationIndex({
    pages,
    index,
    formik,
  });

  const next = useNextDisabled({ pages, index, formik, demographicsFormik });

  const handleScrollUp = () => {
    const element = document.getElementById("surveyWrapper");
    element?.scrollTo({
      top: 0,
      left: 0,
    });
  };

  const handlePrev = () => {
    const currentPageId = pages[index]?.uid;
    const currentPage = {
      ...(formik.values[currentPageId] || {}),
    };
    delete currentPage.viewed;

    formik.setFieldValue(currentPageId, currentPage);

    setIndex((e) => (previousIndex !== -1 ? previousIndex : e - 1));
    handleScrollUp();
    setFullscreenMap(false);
  };

  const handleNext = () => {
    handleProcessNext()
      .then(async (success: boolean) => {
        if (success) {
          setIndex(nextIndex);
          handleScrollUp();
          setFullscreenMap(false);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };
  const inactiveText = {
    before: `${t("survey:surveyNotOpenYet")} ${formattedStartDate}`,
    after: `${t("survey:surveyClosed")} ${formattedEndDate}`,
    notYetScheduled: t("survey:surveyNotYetScheduled"),
  }[phase];

  let activeText = {
    intro: t("survey:startSurvey"),
    surveyPage: t("global:next"),
    demographics: t("global:next"),
  }[currentPageRole];

  let onClickPrev = handlePrev;
  let onClickNext = handleNext;
  let disabled = !!(
    next.disabled ||
    submitting ||
    featureId ||
    openDescription ||
    phase !== "while"
  );
  let disabledNotice = next.message.current;
  const disabledPrev = false;
  const disabledPrevNotice = "";
  let trailingIcon: IconName = "arrowhead-right-md";

  if (currentPageRole === "intro") {
    if (surveyLoginFormShown) {
      const providerMap = {
        "google.com": {
          text: t("auth:authOptions.googleLogin"),
          icon: "logo/Google",
          provider: "google",
        },
        "facebook.com": {
          text: t("auth:authOptions.facebookLogin"),
          icon: "logo/Facebook",
          provider: "facebook",
        },
        password: {
          text: t("auth:login"),
          provider: "email",
        },
      };
      const { text, icon, provider } =
        providerMap[loginFormik.values.providerId] || {};
      disabled = !loginFormik.isValid;
      [disabledNotice] = Object.values(loginFormik.errors || {});
      activeText = text || t("auth:register");
      onClickPrev = () => {
        setSurveyLoginFormShown?.(false);
      };
      onClickNext = async () => {
        setSubmitting(true);
        try {
          const user = await (provider
            ? signIn({
                provider,
                formikStore: loginFormik,
                project,
              })
            : createUser(loginFormik));

          handleProcessNext(user).then((success: boolean) => {
            if (success) {
              setIndex((e) => e + 1);
              handleScrollUp();
              setFullscreenMap(false);
              setSurveyLoginFormShown?.(false);
            }
          });
        } catch (error) {
          console.log(error);

          /**
           * @todo check if || t("auth:error.wrongPassword") is necessary
           */

          loginFormik.setErrors({
            password: t(error) || t("auth:error.wrongPassword"),
          });
        } finally {
          setSubmitting(false);
        }
      };
    }
  }
  if (isLastPage) {
    activeText = t("global:submit");
    trailingIcon = "paperplane";
  }

  // useOnKeyDown((e) => {
  //   if (e.code === "Enter" && !disabled) {
  //     e.preventDefault();
  //     onClickNext();
  //   }
  // });

  if ((featureId && isMobile) || currentPageRole === "outro") return null;

  // if (currentPageRole === "demographics") {
  //   // if (user?.profileCompleted && !disabled) handleNext();
  //   const { providerId } = demographicsFormik.values;
  //   if (providerId?.length) {
  //     disabled = !demographicsFormik.isValid;
  //     [disabledNotice] = Object.values(demographicsFormik.errors);
  //   }
  // }
  return (
    <NavigationButtons
      className={
        isMobile
          ? "border-border-subdued z-50 flex w-full justify-end border-t bg-white"
          : ""
      }
      prev={{
        disabled: disabledPrev,
        disabledNotice: disabledPrevNotice,
        show:
          (currentPageRole !== "intro" && pages.length > 1) ||
          !!surveyLoginFormShown,
        onClick: onClickPrev,
        ariaLabel: t("global:previous"),
      }}
      next={{
        show: pages.length > 0,
        onClick: onClickNext,
        disabled,
        disabledNotice,
        loading: submitting,
        trailingIcon,
        text: phase === "while" ? activeText : inactiveText,
        ariaLabel: t("global:next"),
      }}
      extraButton={
        <div className="flex items-center gap-2">
          {project?.settings?.languages?.length &&
          currentPageRole === "intro" ? (
            <LanguageButton project={project} size="lg" />
          ) : null}
          {project?.settings?.accessibility && currentPageRole === "intro" ? (
            <AccessibilitySettings
              project={project}
              size="lg"
              variant="basic"
            />
          ) : null}
        </div>
      }
    />
  );
};
const ViewSurvey: FC<ViewSurveyProps> = (props) => {
  const { t } = useTranslation();
  const { mode, index, survey, surveyMode } = props;
  const { pages } = survey || {};
  const isMobile = useIsMobile();
  const [openDescription, setOpenDescription] = useState(false);
  const [featureId, setFeatureId] = useState<string>();

  const [fullscreenMap, setFullscreenMap] = useState(false);

  const { currentPage, currentPageRole, progress } = usePrepareSurveyPage({
    pages,
    index,
  });

  if (!pages) return null;

  return (
    <main>
      <form
        className="h-screen w-screen"
        id="viewSurvey"
        aria-label={`${t("global:survey")}: ${survey.title}`}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {(!featureId || !isMobile) && (
          <ProgressBar
            value={progress}
            aria-label={t("survey:step", {
              step: index + 1,
              total: pages.length,
            })}
            round={false}
            className="fixed top-0 z-50 w-full"
          />
        )}
        <CloseSurvey {...props} fullscreenMap={fullscreenMap} mode={mode} />
        <SurveyWrapper
          {...props}
          pages={pages}
          activePage={pages[index]?.uid}
          mode="display"
          isMobile={isMobile}
          openDescription={openDescription}
          setOpenDescription={setOpenDescription}
          featureId={featureId}
          setFeatureId={setFeatureId}
          fullscreenMap={fullscreenMap}
          setFullscreenMap={setFullscreenMap}
        />

        <SurveyNavigationButtons
          {...props}
          isMobile={isMobile}
          currentPage={currentPage}
          currentPageRole={currentPageRole}
          featureId={featureId}
          openDescription={openDescription}
          setFullscreenMap={setFullscreenMap}
          project={props?.project}
        />
      </form>
      <Toast
        leadingIcon="alert-circle-fill"
        variant="critical"
        show={
          (surveyMode === "test" || surveyMode === "preview") &&
          currentPageRole === "intro"
        }
        className="left-0 right-auto"
      >
        {t("survey:testLinkDescription")}
      </Toast>
      <SurveyGuide
        name={currentPage?.qtype}
        hide={!!featureId}
        onRefresh={!!currentPage?.qtype}
      />
    </main>
  );
};

export default ViewSurvey;
